<template>
  <div class="manager-company-global-filters-ui">
    <div class="manager-company-global-filters-ui__selectors">
      <div class="manager-company-global-filters-ui__selector">
        <VHeading :level="3" class="manager-global-filters">Department</VHeading>
        <VRadioControl
          :key="`type-${unsavedChangesGuard.renderKey.value}`"
          :model-value="selectedType"
          :options="SALE_TYPE_OPTIONS"
          name="department"
          :display-mode="EDisplayMode.BUTTON_GROUP"
          @update:model-value="handleTypeChange"
        />
      </div>
      <div class="manager-company-global-filters-ui__selector">
        <div class="manager-company-global-filters-ui__area-header">
          <VHeading :level="3" class="manager-global-filters">Area</VHeading>
          <VCheckbox
            v-if="patchDataClient"
            :key="`bulk-${unsavedChangesGuard.renderKey.value}`"
            :model-value="isBulkEditMode"
            label="Edit multiple areas"
            class="manager-global-filters-areas"
            @update:model-value="handleBulkModeChange"
          />
        </div>
        <VSpinner v-if="!patchDataClient" />
        <PatchInput
          v-else
          :key="`patch-${unsavedChangesGuard.renderKey.value}-${isBulkEditMode}-${selectedType}`"
          :model-value="isBulkEditMode ? selectedPatchIds : selectedPatchId"
          :data-client="patchDataClient"
          :fetch-items-by-ids="fetchPatchesByIds"
          :multiple="isBulkEditMode"
          :show-select-all="isBulkEditMode"
          :close-on-select="!isBulkEditMode"
          label=""
          variant-class-name="manager-global-filters"
          @update:model-value="handlePatchChange"
        />
      </div>
    </div>
    <div v-if="canShowFilters" class="manager-company-global-filters-ui__content">
      <VSpinner v-if="filtersState.isLoading.value" />
      <VFallbackText v-else-if="filtersState.hasError.value">
        Failed to load filters. <VBtn flat @click="filtersState.load">Retry</VBtn>
      </VFallbackText>
      <template v-else>
        <div v-if="isBulkEditMode" class="manager-company-global-filters-ui__bulk-banner">
          <p class="manager-company-global-filters-ui__bulk-banner-title">
            Editing {{ selectedPatchIds.length }} areas
            <VIconTooltip :icon-src="infoIconUrl">
              <ul class="manager-company-global-filters-ui__area-list">
                <li v-for="name in selectedAreaNames" :key="name">{{ name }}</li>
              </ul>
            </VIconTooltip>
          </p>
          <p class="manager-company-global-filters-ui__bulk-banner-text">
            Bulk editing uses default filter values. Saving will replace any existing filters for
            all selected areas.
          </p>
        </div>
        <ListingsFiltersContent
          v-model="filtersState.filters.value"
          date-field-label="Time On Market"
          :tabs="tabs"
          :is-external-company="isExternalCompany"
          :apply-button-label="applyButtonLabel"
          :opportunity-data-client="managerOpportunityDataClient"
          @apply="handleApply"
          @clear="handleClear"
        />
      </template>
    </div>
    <VFallbackText v-else-if="patchDataClient" class="centered">
      Select a department and area to configure filters
    </VFallbackText>
    <UnsavedChangesModal
      v-if="unsavedChangesGuard.showConfirmation.value"
      @action="handleUnsavedChangesAction"
    />
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { ShallowRef } from 'vue';
  import type { TStoredFilters } from '~/misc/listings/types';
  import type { ITab } from '~/misc/v-tabs/types';
  import type { TPatch, TPatchType } from '~/misc/patch/types';
  import type { ICompanyPatchesCache } from '~/modules/manager/company/global-filters/composables/use-company-patches/types';
  import type { IUnsavedChangesGuard } from '~/modules/manager/company/global-filters/composables/use-unsaved-changes-guard/types';
  import type { IGlobalFiltersState } from '~/modules/manager/company/global-filters/composables/use-global-filters-state/types';
  import { ESaleType } from '~/misc/listings/types';
  import { EDisplayMode } from '~/misc/v-radio-control/types';

  // Composables
  import { useUnsavedChangesGuard } from './composables/use-unsaved-changes-guard';
  import { useCompanyPatchesCache } from './composables/use-company-patches';
  import { useGlobalFiltersState } from './composables/use-global-filters-state';
  import { EConfirmationAction } from './unsaved-changes-modal/types';
  import { useToast } from '~/composables/use-toast';

  // Config
  import { TABS } from '~/misc/listings/filters/config';
  import { SALE_TYPE_OPTIONS } from '~/modules/manager/company/global-filters/config';

  // Services
  import { ManagerOpportunityDataClient } from '~/modules/manager/services/manager-opportunity-data-client';
  import { GlobalFiltersPatchDataClient } from './services/global-filters-patch-data-client';

  // Components
  import ListingsFiltersContent from '~/misc/listings/filters/content/ListingsFiltersContent.vue';
  import VRadioControl from '~/misc/v-radio-control/VRadioControl.vue';
  import VHeading from '~/misc/v-heading/VHeading.vue';
  import PatchInput from '~/misc/patch/input/PatchInput.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VBtn from '~/misc/v-btn';
  import VCheckbox from '~/misc/v-checkbox';
  import VIconTooltip from '~/misc/v-icon-tooltip/VIconTooltip.vue';

  // Assets
  import infoIconUrl from '~/assets/images/icons/info-icon.svg';

  const UnsavedChangesModal = defineAsyncComponent(
    () =>
      import(
        '~/modules/manager/company/global-filters/unsaved-changes-modal/UnsavedChangesModal.vue'
      )
  );

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    isExternalCompany: {
      type: Boolean,
      default: false,
    },
  });

  const managerOpportunityDataClient: ManagerOpportunityDataClient =
    new ManagerOpportunityDataClient();
  const { error: showError, success: showSuccess } = useToast();

  const tabs: ITab[] = [...TABS];

  const selectedType: Ref<ESaleType> = ref<ESaleType>(ESaleType.SALES);
  const selectedPatchId: Ref<number | undefined> = ref<number | undefined>(undefined);
  const selectedPatchIds: Ref<number[]> = ref<number[]>([]);
  const isBulkEditMode: Ref<boolean> = ref<boolean>(false);

  const companyIdRef: Ref<number | null> = toRef(() => props.companyId);

  const patchesCache: ICompanyPatchesCache = useCompanyPatchesCache();

  const patchDataClient: ShallowRef<GlobalFiltersPatchDataClient | undefined> = shallowRef<
    GlobalFiltersPatchDataClient | undefined
  >(undefined);

  const unsavedChangesGuard: IUnsavedChangesGuard<TStoredFilters> =
    useUnsavedChangesGuard<TStoredFilters>({
      extractCurrentState: () => filtersState.extractStoredFilters(),
      onSave: handleApply,
    });

  const filtersState: IGlobalFiltersState = useGlobalFiltersState({
    companyId: companyIdRef,
    selectedType,
    selectedPatchId,
    isExternalCompany: props.isExternalCompany,
    onStateSnapshot: () => unsavedChangesGuard.snapshotState(),
  });

  const canShowFilters: ComputedRef<boolean> = computed<boolean>(() => {
    if (!selectedType.value) return false;

    if (isBulkEditMode.value) return selectedPatchIds.value.length > 0;

    return !!selectedPatchId.value;
  });

  const applyButtonLabel: ComputedRef<string> = computed<string>(() => {
    if (filtersState.isSaving.value) return 'Saving...';

    if (isBulkEditMode.value) {
      return `Apply to ${selectedPatchIds.value.length} areas`;
    }

    return 'Save';
  });

  const selectedAreaNames: ComputedRef<string[]> = computed<string[]>(() => {
    return patchesCache.patches.value
      .filter((patch: TPatch) => selectedPatchIds.value.includes(patch.id))
      .map((patch: TPatch) => patch.name);
  });

  const findPatchById = (id: number): TPatch | undefined => {
    return patchesCache.patches.value.find((patch: TPatch) => patch.id === id);
  };

  const fetchPatchesByIds = async (ids: (string | number)[]): Promise<TPatch[]> => {
    const result: TPatch[] = [];
    const missingIds: (string | number)[] = [];

    for (const id of ids) {
      const cached: TPatch | undefined = findPatchById(Number(id));

      if (cached) {
        result.push(cached);
      } else {
        missingIds.push(id);
      }
    }

    if (missingIds.length > 0 && patchDataClient.value) {
      const fetched: TPatch[] = await patchDataClient.value.fetchByIds(missingIds);
      result.push(...fetched);
    }

    return result;
  };

  const isPatchCompatible = (patchType: TPatchType, dept: ESaleType): boolean => {
    if (patchType === 'both') return true;

    if (dept === ESaleType.SALES) return patchType === 'sale';

    return patchType === 'let';
  };

  async function handleApply(): Promise<boolean> {
    const patchIds: number[] = isBulkEditMode.value
      ? selectedPatchIds.value
      : selectedPatchId.value
      ? [selectedPatchId.value]
      : [];

    const success: boolean = await filtersState.save(patchIds);

    if (success) {
      const message: string = isBulkEditMode.value
        ? `Filters applied to ${patchIds.length} areas`
        : 'Filters saved successfully';
      showSuccess({ message });
    } else {
      showError({
        message:
          'Failed to save filters. Please refresh and try again or alterntively please login again if the error persists',
      });
    }

    return success;
  }

  const handleClear = (): void => {
    filtersState.reset();
  };

  const handleTypeChange = (value: string | number | boolean): void => {
    const newType: ESaleType = value as ESaleType;
    const wasIntercepted: boolean = unsavedChangesGuard.interceptChange(newType, (v: ESaleType) => {
      selectedType.value = v;
      patchDataClient.value?.setTypeFilter(v);
      clearIncompatiblePatches(v);
    });

    if (!wasIntercepted) {
      filtersState.load();
    }
  };

  const clearIncompatiblePatches = (dept: ESaleType): void => {
    if (isBulkEditMode.value) {
      const compatibleIds: number[] = selectedPatchIds.value.filter((id: number) => {
        const patch: TPatch | undefined = findPatchById(id);

        return patch && isPatchCompatible(patch.type, dept);
      });

      if (compatibleIds.length !== selectedPatchIds.value.length) {
        selectedPatchIds.value = compatibleIds;
      }
    } else if (selectedPatchId.value) {
      const patch: TPatch | undefined = findPatchById(selectedPatchId.value);

      if (patch && !isPatchCompatible(patch.type, dept)) {
        selectedPatchId.value = undefined;
      }
    }
  };

  const handlePatchChange = (value: string | number | (string | number)[] | undefined): void => {
    if (isBulkEditMode.value) {
      const newPatchIds: number[] = (value as number[]) || [];
      selectedPatchIds.value = newPatchIds;

      if (newPatchIds.length > 0) {
        filtersState.reset();
        unsavedChangesGuard.snapshotState();
      }

      return;
    }

    const newPatchId: number | undefined = value as number | undefined;
    const wasIntercepted: boolean = unsavedChangesGuard.interceptChange(
      newPatchId,
      (v: number | undefined) => {
        selectedPatchId.value = v;
        autoSwitchDepartment(v);
      }
    );

    if (!wasIntercepted) {
      filtersState.load();
    }
  };

  const autoSwitchDepartment = (patchId: number | undefined): void => {
    if (!patchId) return;

    const patch: TPatch | undefined = findPatchById(patchId);

    if (!patch || patch.type === 'both') return;

    const targetDept: ESaleType = patch.type === 'sale' ? ESaleType.SALES : ESaleType.LETTINGS;

    if (selectedType.value !== targetDept) {
      selectedType.value = targetDept;
    }
  };

  const handleBulkModeChange = (newValue: boolean): void => {
    unsavedChangesGuard.interceptChange(newValue, (v: boolean) => {
      isBulkEditMode.value = v;

      if (v) {
        // Entering bulk mode - clear single selection, reset filters
        selectedPatchId.value = undefined;
        selectedPatchIds.value = [];
      } else {
        // Exiting bulk mode - clear multi selection
        selectedPatchIds.value = [];
        selectedPatchId.value = undefined;
      }

      filtersState.reset();
      unsavedChangesGuard.snapshotState();
    });
  };

  const handleUnsavedChangesAction = (action: EConfirmationAction): void => {
    unsavedChangesGuard.handleConfirmation(action, filtersState.load);
  };

  watch(
    companyIdRef,
    (newCompanyId: number | null) => {
      if (newCompanyId) {
        patchDataClient.value = new GlobalFiltersPatchDataClient(
          newCompanyId,
          patchesCache.addPatches
        );
        patchDataClient.value.setTypeFilter(selectedType.value);
      } else {
        patchDataClient.value = undefined;
      }
    },
    { immediate: true }
  );
</script>
